<template>
    <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
        <div
            class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
            <div class="d-sm-flex align-items-center" style="width:100%;">
                <Form ref="etatForm" :validation-schema="etatSchema" style="width:100%;">
                    <div class="row">
                        <div class="col-md-4">
                            <div class="form-group mb-15 mb-sm-20 mb-md-25">
                                <label class="d-block text-black fw-semibold mb-10">
                                    Date Début
                                </label>
                                <Field name="debut" type="date" v-model="debut" style="background-color: #fff;"
                                    class="form-control shadow-none fs-md-15 text-black"
                                    placeholder="Choisisser la date" />
                                <ErrorMessage name="debut" class="text-danger" />
                            </div>
                        </div>
                        <div class="col-md-4">
                            <div class="form-group mb-15 mb-sm-20 mb-md-25">
                                <label class="d-block text-black fw-semibold mb-10">
                                    Date Fin
                                </label>
                                <Field name="fin" v-model="fin" type="date" style="background-color: #fff;"
                                    class="form-control shadow-none fs-md-15 text-black"
                                    placeholder="Choisisser la date" />
                                <ErrorMessage name="fin" class="text-danger" />
                            </div>
                        </div>
                        <div class="col-md-4">
                            <div class="form-group mb-15 mb-sm-20 mb-md-25">
                                <label class="d-block text-black fw-semibold mb-10">
                                    Statut de la commande
                                </label>
                                <Field name="statutCommande" as="select" v-model="statutCommande"
                                    class="form-select shadow-none fs-md-15 text-black">
                                    <option v-for="option in statutOptions" :key="option.value" :value="option.value">
                                        {{ option.label }}
                                    </option>
                                </Field>
                                <ErrorMessage name="statutCommande" class="text-danger" />
                            </div>
                        </div>
                    </div>
                </Form>
            </div>
        </div>
        <div class="card-body p-15 p-sm-20 p-md-25">
            <div class="table-responsive">
                <table class="table text-nowrap align-middle mb-0">
                    <thead>
                        <tr>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                Ref.Cmd
                            </th>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                Date Cmd
                            </th>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                Client
                            </th>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                Montant total (FCFA)
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(commande, index) in commandes" :key="index">
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                                {{ commande.refCom }}
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                                {{ format_Date(commande.dateCommande) }}
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                                <span v-if="commande && commande.client">
                                    {{ commande.client.nomClient }} {{ commande.client.prenomClient }}
                                </span>
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                                {{ commande.montantTotal }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="pagination-area d-md-flex mt-15 mt-sm-20 mt-md-25 justify-content-between align-items-center">
                <PaginationComponent :page="page" :totalPages="totalPages" :totalElements="totalElements" :limit="limit"
                    @paginate="handlePaginate" />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, Ref, watch } from "vue";
import ApiService from "@/services/ApiService";
import { error, format_Date } from "@/utils/utils";
import PaginationComponent from '@/components/Utilities/Pagination.vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import JwtService from "@/services/JwtService";
import * as Yup from 'yup';

export default defineComponent({
    name: "EtatChiffreAffaireClient",
    components: {
        Form,
        Field,
        ErrorMessage,
        PaginationComponent
    },
    setup() {
        onMounted(() => {
            getAllCommandes(1, limit.value, debut.value, fin.value, statutCommande.value);
        });

        const formatDate = (date: Date): string => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        const etatForm = ref<any | null>({});

        const etatSchema = Yup.object().shape({
            statutCommande: Yup.string().notRequired(),
            debut: Yup.date()
                .nullable()
                .transform((value, originalValue) => (originalValue === "" ? null : value))
                .notRequired(),
            fin: Yup.date()
                .nullable()
                .transform((value, originalValue) => (originalValue === "" ? null : value))
                .notRequired()
                .min(
                    Yup.ref('debut'),
                    "La date de fin doit être supérieure ou égale à la date de début"
                ),
        });

        const debut = ref(formatDate(new Date()));
        const fin = ref(formatDate(new Date()));
        const statutCommande: Ref<string> = ref("Payée");
        const commandes = ref<Array<any>>([]);

        // Une commande est soit Payée, soit Annulée
        const statutOptions = [
            { value: 'Payée', label: 'Payée' },
            { value: 'Annulée', label: 'Annulée' },
        ];

        const page = ref(1);
        const totalPages = ref(0);
        const limit = ref(10);
        const totalElements = ref(0);

        watch(
            () => [debut.value, fin.value, statutCommande.value],
            () => {
                if (debut.value && fin.value && new Date(fin.value) < new Date(debut.value)) {
                    return;
                }
                page.value = 1;
                getAllCommandes(1, limit.value, debut.value, fin.value, statutCommande.value);
            }
        );

        const handlePaginate = ({ page_, limit_ }) => {
            page.value = page_;
            getAllCommandes(page_, limit_, debut.value, fin.value, statutCommande.value);
        };

        function getAllCommandes(page = 1, limi = 10, debut = '', fin = '', statut = '') {
            ApiService.get(`/etatChiffreAffaireCommande?start=${page}&limit=${limi}&debut=${debut}&fin=${fin}&statutCommande=${encodeURIComponent(statut)}`)
                .then(({ data }) => {
                    commandes.value = data.data.data;
                    totalPages.value = data.data.totalPages;
                    limit.value = data.data.limit;
                    totalElements.value = data.data.totalElements;
                    return data.data;
                })
                .catch(({ response }) => {
                    error(response?.data?.message || "Erreur lors de la récupération des commandes");
                });
        }

        const privileges = ref<Array<string>>(JwtService.getPrivilege());

        const checkPermission = (name) => {
            return privileges.value.includes(name);
        }

        return {
            commandes,
            checkPermission,
            getAllCommandes,
            page,
            format_Date,
            totalPages,
            limit,
            statutCommande,
            statutOptions,
            debut, fin, etatSchema,
            totalElements,
            handlePaginate,
            etatForm
        };
    },
});
</script>